import React, { useRef, useEffect } from 'react';
import { func, shape } from 'prop-types';
import { createPortal } from 'react-dom';

import { Card, CardContent } from '@andes/card';

import { NAMESPACE } from './constants';
import useMapConfig from '../../hooks/use-map-config';
import { useStaticProps } from '../../../context/static-props';
import { trackClickEvent } from '../../track';

const namespace = `${NAMESPACE}-draw-bar`;

/**
 * Component to display the draw bar overlay during polygon drawing mode.
 * This overlay provides options to cancel the current drawing state.
 *
 * @param {Object} props
 * @param {Function} props.handleDrawable - Callback to toggle drawing mode.
 * @param {Object} [props.map] - Map instance used to mount the overlay (expects getDiv method).
 * @returns {React.Component|null} The DrawBar component or null if no reference or label.
 */

const DrawBar = ({ handleDrawable, map }) => {
  const {
    controls: { drawBar: { label, action_bar: actionLabel, track } = {} } = {},
    track: { polygonSearch: melidata } = {},
  } = useMapConfig() || {};
  const { deviceType } = useStaticProps();

  const trackInfo = track || melidata;

  const drawBarRef = useRef(document.createElement('div'));

  const onClick = () => {
    if (trackInfo) {
      trackClickEvent(null, trackInfo.melidata);
    }

    handleDrawable();
  };

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const mapContainer = map.getDiv();
    const currentRef = drawBarRef.current;

    currentRef.className = `${namespace}-overlay`;

    mapContainer.appendChild(currentRef);

    return () => {
      if (mapContainer.contains(currentRef)) {
        mapContainer.removeChild(currentRef);
      }
    };
  }, [map, drawBarRef]);

  if (!drawBarRef) {
    return null;
  }

  return (
    label &&
    createPortal(
      <Card className={`${namespace}-card`} shadow="elevated" paddingSize={0}>
        <CardContent className={`${namespace}-card-content`}>
          <span className={`${namespace}-card-content-label`}>{label}</span>
          <button type="button" onClick={onClick} className={`${namespace}-button-cancel`}>
            {deviceType === 'desktop' && <span className={`${namespace}-button-icon`}>X</span>}
            {actionLabel && <span>{actionLabel}</span>}
          </button>
        </CardContent>
      </Card>,
      drawBarRef.current,
    )
  );
};

DrawBar.propTypes = {
  handleDrawable: func.isRequired,
  map: shape({
    getDiv: func,
  }),
};

DrawBar.defaultProps = {
  map: null,
};

export default DrawBar;
